import axios from 'axios';
import { API_PATHS } from './apiEndpoints';

// Lấy URL backend từ config.js
const BACKEND_API_URL = window.AMS_CONFIG?.API_URL || process.env.REACT_APP_BACKEND_URL || '';
const API_PATH = window.AMS_CONFIG?.API_PATH || '/api';

// Logs for tracking initialization
console.log('API module initializing:', { 
  BACKEND_API_URL, 
  API_PATH,
  fullUrl: `${BACKEND_API_URL}${API_PATH}` 
});

// Create an axios instance with default config
const api = axios.create({
  baseURL: `${BACKEND_API_URL}${API_PATH}`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
  withCredentials: true, // Enable credentials for CORS
});

// Request interceptor for API logging
api.interceptors.request.use(config => {
  console.log(`API Request: ${config.method} ${config.url}`);
  return config;
});


// Request interceptor for logging and token handling
api.interceptors.request.use(
  (config) => {
    // Log all requests
    console.log('API Request:', config.method, config.url);
    
    // Add authorization token
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    // Debug response from server for troubleshooting
    console.log('Response from ' + response.config.url + ': Status ' + response.status);
    return response;
  }, error => {
    if (error.response) {
      // Server responded with a status code other than 2xx
      console.error('API Error:', error.message);
      console.error('Failed URL:', error.config.url);
      
      // Handle token expiry - attempt to refresh token
      if (error.response.status === 401 && error.config.url !== '/token/refresh/') {
        // Try to refresh the token
        return refreshTokenAndRetry(error);
      }
      
      // General error message for other errors
      console.error(`API Error (${error.response.status}): ${error.response.data?.detail || error.response.data?.error || 'API request failed'}`);
      
      // Return the error for handling by the caller
      return Promise.reject(error);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Network Error: No response received');
      return Promise.reject(error);
    } else {
      // Error in setting up the request
      console.error('Request Error:', error.message);
      return Promise.reject(error);
    }
  }
);

// Function to refresh token and retry the original request
async function refreshTokenAndRetry(error) {
  try {
    // Get the refresh token
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      // No refresh token available, logout user
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Try to get a new access token
    const response = await axios.post('/api/token/refresh/', {
      refresh: refreshToken
    });
    
    // Store the new access token
    localStorage.setItem('accessToken', response.data.access);
    
    // Update the Authorization header for the original request
    error.config.headers['Authorization'] = `Bearer ${response.data.access}`;
    
    // Retry the original request
    return api(error.config);
  } catch (refreshError) {
    // Token refresh failed, logout user
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
    return Promise.reject(error);
  }
}

// Format campaign data for API requests
const formatCampaignData = (campaignData) => {
    // Map field names to what the backend expects, using exact format required by database
  return {
      // Required fields - ensure correct type and format 
      campaign_name: campaignData.campaign_name,
      // Use 'brand' field as expected by backend API
      brand: typeof campaignData.brand === 'string' ? 
        parseInt(campaignData.brand, 10) : campaignData.brand,
      
      // Use media_types_input if already formatted, otherwise format from media_types
      media_types_input: campaignData.media_types_input || 
        (Array.isArray(campaignData.media_types) 
          ? campaignData.media_types.map(type => {
              // Ensure each media type is a string
              if (typeof type === 'string') {
                return type;
              } else if (typeof type === 'object' && type !== null) {
                // Extract name from various possible formats
                const mediaTypeName = type.name || type.media_type || type.media_type_channel_name;
                return typeof mediaTypeName === 'string' ? mediaTypeName : 'Digital';
              }
              return 'Digital'; // Default
            })
          : []),
        
      // Preserve existing total_given or use 0 as fallback for new campaigns only
      ...(campaignData.total_given !== undefined && { total_given: campaignData.total_given }),
        
      // Optional fields - only include if they have values with proper format
      // Use 'sub_brand' field as expected by backend API
      ...(campaignData.sub_brand && { 
        sub_brand: typeof campaignData.sub_brand === 'string' ? 
          parseInt(campaignData.sub_brand, 10) : campaignData.sub_brand 
      }),
      ...(campaignData.objective && { objective: campaignData.objective }),
      
      // Use campaign_status field
      ...(campaignData.campaign_status && { 
        campaign_status: campaignData.campaign_status
      }),
      // Fallback to status field if campaign_status is not provided
      ...(!campaignData.campaign_status && campaignData.status && {
        campaign_status: campaignData.status
      }),
      
      // Format dates as YYYY-MM-DD strings
      ...(campaignData.start_date && { 
        start_date: typeof campaignData.start_date === 'object' ? 
          campaignData.start_date.toISOString().split('T')[0] : campaignData.start_date 
      }),
      ...(campaignData.end_date && { 
        end_date: typeof campaignData.end_date === 'object' ? 
          campaignData.end_date.toISOString().split('T')[0] : campaignData.end_date 
      }),
      
      // Campaign type using correct field mapping
      ...(campaignData.campaign_type && { campaign_type: campaignData.campaign_type }),
      
      // Preserve planning and actual totals if they exist
      ...(campaignData.planning_total !== undefined && { planning_total: campaignData.planning_total }),
      ...(campaignData.actual_total !== undefined && { actual_total: campaignData.actual_total }),
      ...(campaignData.saving_total !== undefined && { saving_total: campaignData.saving_total }),
      ...(campaignData.unused_total !== undefined && { unused_total: campaignData.unused_total }),
      
      // Other database fields
      ...(campaignData.campaign_naming_taxonomy && { campaign_naming_taxonomy: campaignData.campaign_naming_taxonomy }),
      ...(campaignData.agency_id && { agency_id: parseInt(campaignData.agency_id, 10) })
    };
};

// API endpoints for campaigns
export const campaignApi = {
  getCampaignsBySnapshot: (snapshotMonth) => {
    return api.get(`/campaigns-data/?snapshot_month=${snapshotMonth}`);
  },
  getCampaignsSnapshot: (snapshotMonth) => {
    return api.get(`/campaigns-data/?snapshot_month=${snapshotMonth}`);
  },
  // Excel-like monthly data endpoint (merged from campaign.py)
  getExcelLikeMonthlyData: (campaignId) => {
    return api.get(`/campaigns-data/${campaignId}/excel-like-monthly-data/`);
  },
  getAllCampaigns: (queryParams = '') => {
    // Handle both full URL with query params and just query params
    const url = queryParams.startsWith('http') 
      ? queryParams 
      : `${API_PATHS.CAMPAIGNS}${queryParams}`;
    return api.get(url);
  },
  getCampaign: (id) => {
    const cleanPath = API_PATHS.CAMPAIGNS.replace(/^\//, '').replace(/\/$/, '');
    return api.get(`/${cleanPath}/${id}/`);
  },
  getCampaigns: (queryParams = '') => api.get(`/${API_PATHS.CAMPAIGNS}${queryParams}`),
  getCampaignById: (id) => api.get(`/${API_PATHS.CAMPAIGNS}${id}/`),
  createCampaign: (formData) => {
    console.log('Creating campaign with data:', formData);
    
    // Format data for API
    const formattedData = formatCampaignData(formData);
    console.log('Formatted campaign data for API:', formattedData);
    
    const cleanPath = API_PATHS.CAMPAIGNS.replace(/^\//, '').replace(/\/$/, '');
    // Đảm bảo endpoint luôn có dấu '/' ở cuối
    return api.post(`/${cleanPath}/`, formattedData)
      .catch(error => {
        console.error('Campaign creation failed with error:', error);
        if (error.response && error.response.data) {
          console.error('Server response data:', error.response.data);
        }
        throw error;
      });
  },
  updateCampaign: (id, formData) => {
    console.log(`Updating campaign ${id} with data:`, formData);
    
    // Format data for API
    const formattedData = formatCampaignData(formData);
    console.log(`Formatted update data for campaign ${id}:`, formattedData);
    
    // Đảm bảo URL có đúng một dấu / ở đầu, không phải hai dấu
    const url = `/${API_PATHS.CAMPAIGNS.replace(/^\/+/, '')}${id}/`;
    return api.put(url, formattedData)
      .catch(error => {
        console.error(`Campaign update failed for ${id} with error:`, error);
        if (error.response && error.response.data) {
          console.error('Server response data:', error.response.data);
        }
        throw error;
      });
  },
  deleteCampaign: (id) => {
    console.log(`Deleting campaign ${id}`);
    // Đảm bảo URL có đúng một dấu / ở đầu, không phải hai dấu
    const url = `/${API_PATHS.CAMPAIGNS.replace(/^\/+/, '')}${id}/`;
    return api.delete(url)
      .catch(error => {
        console.error(`Campaign deletion failed for ${id} with error:`, error);
        if (error.response && error.response.data) {
          console.error('Server response data:', error.response.data);
        }
        throw error;
      });
  },
  getCampaignMediaTypes: (campaignId) => api.get(`/${API_PATHS.MEDIA_TYPES_BY_CAMPAIGN(campaignId)}`),
};

// API endpoints for campaign media types
export const mediaTypeApi = {
  getCampaignMediaTypes: (campaignId) => api.get(`/${API_PATHS.MEDIA_TYPES_BY_CAMPAIGN(campaignId)}`),
  
  // Check if a media type already exists for a campaign
  checkMediaTypeExists: async (campaignId, mediaTypeName) => {
    try {
      // Get existing media types
      const response = await api.get(`/${API_PATHS.MEDIA_TYPES_BY_CAMPAIGN(campaignId)}`);
      const existingMediaTypes = response.data || [];
      
      // Normalize the media type name for case-insensitive comparison
      const normalizedName = mediaTypeName.trim().toLowerCase();
      
      // Check if any existing media type matches (case-insensitive)
      const exists = existingMediaTypes.some(mediaType => {
        const existingName = (mediaType.media_type || '').toLowerCase();
        return existingName === normalizedName;
      });
      
      return {
        exists,
        existingMediaTypes
      };
    } catch (error) {
      console.error(`[ERROR] Failed to check if media type exists for campaign ${campaignId}:`, error);
      return {
        exists: false,
        existingMediaTypes: [],
        error
      };
    }
  },
  
  // Check if media type has accrual data
  hasAccrualData: async (campaignId, mediaTypeId) => {
    try {
      // Query accrual data to see if any exists for this media type
      const response = await api.get(`/${API_PATHS.ACCRUAL_DETAIL(campaignId)}`);
      const accrualData = response.data || [];
      
      // Check if any accrual row references this media type
      const hasData = accrualData.some(row => 
        row.campaign_media_type === mediaTypeId || 
        // Also check media_type field if available
        (row.media_type && mediaTypeId.toString().includes(row.media_type))
      );
      
      return hasData;
    } catch (error) {
      console.error(`[ERROR] Failed to check if media type has accrual data: ${mediaTypeId}`, error);
      // Assume it has data to be safe (don't delete if unsure)
      return true;
    }
  },
  
  // Find and remove duplicate media types that have no data
  cleanupDuplicateMediaTypes: async (campaignId) => {
    try {
      console.log(`[INFO] Cleaning up duplicate media types for campaign ${campaignId}`);
      
      // Get all media types for this campaign
      const response = await api.get(`/${API_PATHS.MEDIA_TYPES_BY_CAMPAIGN(campaignId)}`);
      const mediaTypes = response.data || [];
      
      if (!mediaTypes.length) {
        return {
          success: true,
          message: "No media types found for this campaign",
          deleted: []
        };
      }
      
      // Group media types by name (case insensitive)
      const mediaTypeGroups = {};
      mediaTypes.forEach(mt => {
        const key = (mt.media_type || '').toLowerCase();
        if (key) {
          if (!mediaTypeGroups[key]) {
            mediaTypeGroups[key] = [];
          }
          mediaTypeGroups[key].push(mt);
        }
      });
      
      // Find groups with more than one media type (duplicates)
      const duplicates = Object.entries(mediaTypeGroups)
        .filter(([_, group]) => group.length > 1)
        .map(([key, group]) => ({ key, mediaTypes: group }));
      
      if (duplicates.length === 0) {
        return {
          success: true, 
          message: "No duplicate media types found",
          deleted: []
        };
      }
      
      console.log(`[INFO] Found ${duplicates.length} duplicate media type groups to clean up`);
      
      // Since direct deletion doesn't work, try updating the campaign to keep only one of each media type
      try {
        // Get the current campaign data
        const campaignResponse = await api.get(`/campaigns-data/${campaignId}/`);
        const campaignData = campaignResponse.data;
        
        // Build a list of unique media types to keep
        const uniqueMediaTypes = [];
        
        // For each group, keep only the first one (lowest ID)
        for (const [, group] of Object.entries(mediaTypeGroups)) {
          // Sort by ID (ascending) and keep only the first one
          const sorted = [...group].sort((a, b) => a.id - b.id);
          uniqueMediaTypes.push({
            media_type: sorted[0].media_type
          });
        }
        
        // Update the campaign with the deduplicated list
        const updatePayload = {
          ...campaignData,
          media_types: uniqueMediaTypes
        };
        
        console.log(`[INFO] Updating campaign with deduplicated media types:`, uniqueMediaTypes);
        await api.put(`/campaigns-data/${campaignId}/`, updatePayload);
        
        return {
          success: true,
          message: `Removed ${mediaTypes.length - uniqueMediaTypes.length} duplicate media types via campaign update`,
          originalCount: mediaTypes.length,
          newCount: uniqueMediaTypes.length,
          uniqueMediaTypes
        };
      } catch (error) {
        console.error(`[ERROR] Failed to update campaign with deduplicated media types:`, error);
        return {
          success: false,
          message: `Error cleaning up duplicates: ${error.message}`,
          error
        };
      }
    } catch (error) {
      console.error(`[ERROR] Failed to clean up duplicate media types:`, error);
      return {
        success: false,
        message: `Error cleaning up duplicates: ${error.message}`,
        error
      };
    }
  },
  
  createMediaType: async (campaignId, mediaTypeData) => {
    console.log(`[DEBUG] Creating media type for campaign ${campaignId} with data:`, JSON.stringify(mediaTypeData));
    
    // Normalize media type name - ensure proper casing and trim whitespace
    const normalizedData = { ...mediaTypeData };
    if (typeof normalizedData.media_type === 'string') {
      // Ensure consistent casing (first char uppercase, rest lowercase)
      normalizedData.media_type = normalizedData.media_type.trim();
      if (normalizedData.media_type.length > 0) {
        normalizedData.media_type = normalizedData.media_type.charAt(0).toUpperCase() + 
                                   normalizedData.media_type.slice(1).toLowerCase();
      }
    }
    
    // First, check if this media type already exists
    try {
      const { exists, existingMediaTypes } = await mediaTypeApi.checkMediaTypeExists(
        campaignId, 
        normalizedData.media_type
      );
      
      if (exists) {
        console.log(`[INFO] Media type "${normalizedData.media_type}" already exists for campaign ${campaignId}, skipping creation`);
        
        // Find the existing media type object
        const existingMediaType = existingMediaTypes.find(mt => 
          mt.media_type.toLowerCase() === normalizedData.media_type.toLowerCase()
        );
        
        // Return a resolved promise with the existing media type
        return Promise.resolve({
          data: existingMediaType,
          message: 'Media type already exists',
          status: 200
        });
      }
    } catch (error) {
      console.error(`[ERROR] Failed to check if media type exists:`, error);
      // Continue with creation attempt even if check fails
    }
    
    // Đường dẫn API và format payload tùy thuộc vào cấu trúc backend
    const paths = [
      // Đường dẫn 1: /campaigns-data/{id}/media-types/
      { 
        url: `/campaigns-data/${campaignId}/media-types/`,
        data: {
          media_type: normalizedData.media_type,
          campaign: campaignId
        }
      },
      // Đường dẫn 2: /campaigns-data/media-types/
      { 
        url: `/campaigns-data/media-types/`,
        data: {
          media_type: normalizedData.media_type,
          campaign: campaignId
        }
      },
      // Đường dẫn 3: /campaigns/{id}/media-types/ (slug khác)
      { 
        url: `/campaigns/${campaignId}/media-types/`,
        data: {
          media_type: normalizedData.media_type,
          campaign: campaignId
        }
      }
    ];
    
    // Log all the paths we're going to try
    console.log(`[DEBUG] Will try the following API paths:`, 
      paths.map(p => `${p.url} with data: ${JSON.stringify(p.data)}`).join(', ')
    );
    
    // Try each path in sequence until one works
    return tryApiPaths(paths);
    
    // Helper function to try multiple API paths in sequence
    function tryApiPaths(pathsToTry, index = 0) {
      if (index >= pathsToTry.length) {
        // All paths failed
        console.error(`[ERROR] All API paths failed for media type creation`);
        return Promise.reject(new Error('All media type API paths failed'));
      }
      
      const currentPath = pathsToTry[index];
      console.log(`[DEBUG] Trying API path ${index + 1}: ${currentPath.url}`);
      
      return api.post(currentPath.url, currentPath.data)
        .then(response => {
          console.log(`[SUCCESS] Media type created successfully at ${currentPath.url}:`, 
            JSON.stringify(response.data)
          );
          return response;
        })
        .catch(error => {
          console.error(`[ERROR] Path ${index + 1} failed: ${currentPath.url}`, error);
          if (error.response && error.response.data) {
            console.error('Response data:', JSON.stringify(error.response.data));
          }
          
          // Try the next path
          return tryApiPaths(pathsToTry, index + 1);
        });
    }
  },
  updateMediaType: (id, mediaTypeData) => 
    api.put(`${API_PATHS.CAMPAIGNS}media-types/${id}/`, mediaTypeData),
  deleteMediaType: async (campaignId, mediaTypeId) => {
    console.log(`[INFO] Deleting media type with ID: ${mediaTypeId} from campaign: ${campaignId}`);
    
    // Include campaign ID in the path if available
    const paths = campaignId ? [
      // NEW ENDPOINT - đặt ở đầu danh sách để được thử trước tiên
      `/campaigns-data/${campaignId}/media-type/${mediaTypeId}/`,
      // These paths include both campaign ID and media type ID
      `/campaigns-data/${campaignId}/media-types/${mediaTypeId}/`,
      `/campaigns/${campaignId}/media-types/${mediaTypeId}/`,
      `/campaigns/${campaignId}/media-type/${mediaTypeId}/`,
      // These are the campaign media type specific endpoints
      `/campaign-media-types/${campaignId}/${mediaTypeId}/`,
      `/campaign_media_type/${campaignId}/${mediaTypeId}/`
    ] : [
      // Fallback to these if no campaign ID is available
      `/campaign-media-type/${mediaTypeId}/`,
      `/campaign-media-types/${mediaTypeId}/`,
      `/campaign_media_type/${mediaTypeId}/`,
      `/campaign_media_types/${mediaTypeId}/`
    ];
    
    // Try each path in sequence until one works
    let lastError = null;
    
    // First try using DELETE with path parameters
    for (const path of paths) {
      try {
        console.log(`[DEBUG] Trying to delete using path: ${path}`);
        const response = await api.delete(path);
        console.log(`[SUCCESS] Successfully deleted media type ID ${mediaTypeId} using path: ${path}`);
        return response;
      } catch (error) {
        console.error(`[ERROR] Failed to delete media type using path ${path}:`, error);
        lastError = error;
      }
    }
    
    // If all path attempts failed, try using POST with a _method=DELETE parameter
    // Some APIs implement deletion this way
    console.log(`[INFO] Trying POST with _method=DELETE parameter`);
    if (campaignId) {
      try {
        const path = `/campaigns-data/${campaignId}/media-types/`;
        const data = {
          _method: 'DELETE',
          id: mediaTypeId,
          media_type_id: mediaTypeId
        };
        console.log(`[DEBUG] Trying POST with _method=DELETE to ${path}`, data);
        const response = await api.post(path, data);
        console.log(`[SUCCESS] Successfully removed media type using POST with _method=DELETE`);
        return response;
      } catch (error) {
        console.error(`[ERROR] Failed with POST _method=DELETE approach:`, error);
        lastError = error;
      }
      
      // Try a media type removal endpoint if available
      try {
        const path = `/campaigns-data/${campaignId}/remove-media-type/`;
        const data = {
          id: mediaTypeId,
          media_type_id: mediaTypeId
        };
        console.log(`[DEBUG] Trying removal endpoint: ${path}`, data);
        const response = await api.post(path, data);
        console.log(`[SUCCESS] Successfully removed media type using removal endpoint`);
        return response;
      } catch (error) {
        console.error(`[ERROR] Failed with removal endpoint:`, error);
        lastError = error;
      }
    }
    
    // If all attempts failed, throw the last error
    throw lastError || new Error('Failed to delete media type');
  },
  
  // Fallback function that tries to delete a media type with the campaign context
  deleteMediaTypeWithCampaign: async (campaignId, mediaTypeId) => {
    console.log(`[INFO] Trying to delete media type ${mediaTypeId} in campaign ${campaignId} context`);
    
    // Try various possible endpoint formats that include the campaign ID
    const paths = [
      `/campaigns-data/${campaignId}/media-types/${mediaTypeId}/`,
      `/campaigns/${campaignId}/media-types/${mediaTypeId}/`,
      `/campaign-media-types/${campaignId}/${mediaTypeId}/`, 
      `/campaigns-data/campaigns/${campaignId}/media-types/${mediaTypeId}/`,
      `/accrual/campaigns/${campaignId}/media-types/${mediaTypeId}/`
    ];
    
    let lastError = null;
    
    for (const path of paths) {
      try {
        console.log(`[DEBUG] Trying to delete media type using campaign-specific path: ${path}`);
        const response = await api.delete(path);
        console.log(`[SUCCESS] Successfully deleted media type ID ${mediaTypeId} for campaign ${campaignId}`);
        return response;
      } catch (error) {
        console.error(`[ERROR] Failed to delete media type using path ${path}:`, error);
        lastError = error;
      }
    }
    
    throw lastError || new Error(`Failed to delete media type ${mediaTypeId} for campaign ${campaignId}`);
  },
};

// API endpoints for accrual monthly data (Excel-like view)
export const accrualApi = {
  getAccrualData: (campaignId) => 
    api.get(`/${API_PATHS.ACCRUAL_DETAIL(campaignId)}`),
  getAccrualDataByMediaType: (campaignId, mediaType) => 
    api.get(`/${API_PATHS.ACCRUAL_DETAIL(campaignId)}?media_type=${mediaType}`),
  
  // NEW: Snapshot accrual data - uses the backend snapshot endpoint
  getSnapshotAccrualData: (campaignId, snapshotMonth, mediaType = null) => {
    console.log(`[SNAPSHOT API] Fetching snapshot accrual data for campaign ${campaignId}, month ${snapshotMonth}, mediaType: ${mediaType}`);
    let url = `/campaigns-data/${campaignId}/accrual-detail/?snapshot_month=${snapshotMonth}`;
    if (mediaType) {
      url += `&media_type=${mediaType}`;
    }
    return api.get(url);
  },
  
  // Row history endpoints
  fetchRowHistory: (rowId) => 
    api.get(`/accrual-detail/${rowId}/history/`),
  // Unlock request endpoints - DEPRECATED: Use lockUnlockApi instead
  // These are kept for backward compatibility but should be migrated to lockUnlockApi
  saveAccrualData: (campaignId, accrualData, options = {}) => {
    // Process accrual data to normalize media types
    const processedData = accrualData.map(item => {
      if (item.media_type) {
        // Capitalize first letter and lowercase the rest
        const normalizedMediaType = item.media_type.charAt(0).toUpperCase() + 
                                   item.media_type.slice(1).toLowerCase();
        return { ...item, media_type: normalizedMediaType };
      }
      return item;
    });
    
    // UUID-based viewset endpoint expects request.data to be a direct list
    // Handle deletions separately if provided
    if (Array.isArray(options.deleted_ids) && options.deleted_ids.length > 0) {
      console.log('⚠️ Warning: Delete operations should be handled separately for UUID endpoint');
      // For now, we'll send just the processedData and handle deletions in the frontend
      // TODO: Consider implementing separate delete calls or using a different endpoint
    }
    
    return api.post(`/${API_PATHS.ACCRUAL_DETAIL(campaignId)}`, processedData);
  },
  updateAccrualData: (campaignId, accrualDataId, accrualData) => {
    console.log(`Updating accrual data for campaign ${campaignId}, row ID ${accrualDataId}:`, JSON.stringify(accrualData));
    
    // Normalize media type if present
    let processedData = { ...accrualData };
    if (processedData.media_type) {
      processedData.media_type = processedData.media_type.charAt(0).toUpperCase() + 
                                processedData.media_type.slice(1).toLowerCase();
    }
    
    // Backend expects POST with array of data, not PUT with individual ID
    // Include the ID in the data so backend knows it's an update
    return api.post(`/${API_PATHS.ACCRUAL_DETAIL(campaignId)}`, { data: [processedData] });
  },
  deleteAccrualData: (campaignId, accrualDataId) => {
    console.log(`Deleting accrual data for campaign ${campaignId}, row ID ${accrualDataId}`);
    // Send the ID in the request body ('data' property in Axios config)
    const url = `/${API_PATHS.ACCRUAL_DETAIL(campaignId)}`; // URL does not include the row ID anymore
    return api.delete(url, { data: { id: accrualDataId } }); 
  },
  
  // History and version management
  getAccrualHistory: (campaignId, page = 1, pageSize = 20) => 
    api.get(`/campaigns-data/${campaignId}/accrual-history/?page=${page}&page_size=${pageSize}`),
  getDataVersions: (campaignId, page = 1, pageSize = 20) => 
    api.get(`/campaigns-data/${campaignId}/versions/?page=${page}&page_size=${pageSize}`),
  restoreVersion: (campaignId, versionId) => 
    api.post(`/campaigns-data/${campaignId}/restore-version/${versionId}/`),
  // Endpoint lưu lịch sử thay đổi cho từng row
  saveRowLog: (logData) => {
    console.log('DEBUG: Backend automatically logs history when saving accrual data');
    // Tương tự như version, backend tự động lưu lịch sử khi lưu accrual data
    // Trả về Promise.resolve để không gây lỗi
    return Promise.resolve({ 
      data: { 
        success: true, 
        message: 'Row history logged automatically by backend' 
      } 
    });
  },
  
  // Lấy lịch sử của một row
  getRowHistory: (rowId) => {
    console.log('DEBUG: Fetching row history for ID:', rowId);
    // Sử dụng đúng endpoint trong backend
    return api.get(`/accrual-detail/${rowId}/history/`);
  },
};

// API endpoints for reference data (legend, lookups)
export const referenceApi = {
  getVendors: () => {
    console.log('Fetching vendors');
    return api.get(API_PATHS.DIGITAL_VENDORS);
  },
  getDigitalAdFormats: () => {
    return api.get(API_PATHS.DIGITAL_AD_FORMATS);
  },
  getOOHFormats: () => {
    return api.get(API_PATHS.OOH_FORMATS);
  },
  getSubMediaTypes: () => {
    console.log('Fetching sub media types');
    return api.get('/category/sub-media-types/');
  },
  getAgencies: () => {
    console.log('Fetching agencies');
    return api.get(API_PATHS.AGENCIES);
  },
  getCampaignTypes: async () => {
    console.log('Fetching campaign types');
    try {
      const response = await api.get(API_PATHS.CAMPAIGN_TYPES);
      console.log('Campaign types API response:', response.data);
      
      // Handle both array and object responses from the API
      let campaignTypesData = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          campaignTypesData = response.data;
        } else if (typeof response.data === 'object') {
          // If response is an object with results or items property
          if (Array.isArray(response.data.results)) {
            campaignTypesData = response.data.results;
          } else if (Array.isArray(response.data.items)) {
            campaignTypesData = response.data.items;
          } else if (Array.isArray(response.data.campaign_types)) {
            campaignTypesData = response.data.campaign_types;
          } else {
            // Try to convert object to array if it has numeric keys
            const possibleArray = Object.values(response.data).filter(item => 
              typeof item === 'object' && (item.campaign_type_id || item.id || item.campaign_type_name)
            );
            if (possibleArray.length > 0) {
              campaignTypesData = possibleArray;
            }
          }
        }
      }
      
      // Map the data to ensure consistent format
      const mappedCampaignTypes = campaignTypesData.map(campaignType => ({
        campaign_type_id: campaignType.campaign_type_id || campaignType.id,
        campaign_type_name: campaignType.campaign_type_name || campaignType.name
      }));
      
      return { data: mappedCampaignTypes };
    } catch (error) {
      console.error('Error fetching campaign types:', error);
      return { data: [], error: error.message };
    }
  },
  getBrands: async () => {
    console.log('Fetching brands from /category/brands endpoint');
    try {
      const response = await api.get(API_PATHS.BRANDS);
      console.log('Brands API response:', response.data);
      
      // Handle both array and object responses from the API
      let brandsData = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          brandsData = response.data;
        } else if (typeof response.data === 'object') {
          // If response is an object with results or items property
          if (Array.isArray(response.data.results)) {
            brandsData = response.data.results;
          } else if (Array.isArray(response.data.items)) {
            brandsData = response.data.items;
          } else if (Array.isArray(response.data.brands)) {
            brandsData = response.data.brands;
          } else {
            // Try to convert object to array if it has numeric keys
            const possibleArray = Object.values(response.data).filter(item => 
              typeof item === 'object' && (item.brand_id || item.id)
            );
            if (possibleArray.length > 0) {
              brandsData = possibleArray;
            }
          }
        }
      }
      
      // Map the data to ensure consistent format
      const mappedBrands = brandsData.map(brand => ({
        brand_id: brand.brand_id || brand.id || brand.brandId,
        brand_name: brand.brand_name || brand.name || brand.brandName
      }));
      
      return { data: mappedBrands };
    } catch (error) {
      console.error('Error fetching brands:', error);
      // Return empty array instead of mock data
      return { data: [], error: error.message };
    }
  },
  getSubBrands: async (brandId) => {
    console.log(`Fetching sub-brands for brand ID: ${brandId}`);
    try {
      // Django REST Framework expects the brand_id parameter
      const response = await api.get(`${API_PATHS.SUB_BRANDS}?brand_id=${brandId}`);
      console.log('Sub-brands API response:', response.data);
      
      // Handle both array and object responses from the API
      let subBrandsData = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          subBrandsData = response.data;
        } else if (typeof response.data === 'object') {
          // If response is an object with results or items property
          if (Array.isArray(response.data.results)) {
            subBrandsData = response.data.results;
          } else if (Array.isArray(response.data.items)) {
            subBrandsData = response.data.items;
          } else if (Array.isArray(response.data.sub_brands)) {
            subBrandsData = response.data.sub_brands;
          } else {
            // Try to convert object to array if it has numeric keys
            const possibleArray = Object.values(response.data).filter(item => 
              typeof item === 'object' && (item.sub_brand_id || item.id)
            );
            if (possibleArray.length > 0) {
              subBrandsData = possibleArray;
            }
          }
        }
      }
      
      // Map the data to ensure consistent format
      const mappedSubBrands = subBrandsData.map(subBrand => ({
        sub_brand_id: subBrand.sub_brand_id || subBrand.id || subBrand.subBrandId,
        sub_brand_name: subBrand.sub_brand_name || subBrand.name || subBrand.subBrandName,
        brand_id: subBrand.brand_id || subBrand.brand || subBrand.brandId
      }));
      
      // Filter sub-brands by brand ID in case the API doesn't filter them
      // Sometimes the API might return all sub-brands regardless of the query parameter
      const filteredSubBrands = mappedSubBrands.filter(subBrand => {
        const subBrandBrandId = parseInt(subBrand.brand_id, 10);
        const targetBrandId = parseInt(brandId, 10);
        const isMatch = subBrandBrandId === targetBrandId;
        
        if (!isMatch) {
          console.log(`Filtering out sub-brand ID ${subBrand.sub_brand_id} because brand ID ${subBrandBrandId} doesn't match target ${targetBrandId}`);
        }
        
        return isMatch;
      });
      
      console.log(`Filtered ${mappedSubBrands.length - filteredSubBrands.length} sub-brands that don't match brand ID ${brandId}`);
      
      return { data: filteredSubBrands };
    } catch (error) {
      console.error(`Error fetching sub-brands for brand ID ${brandId}:`, error);
      return { data: [], error: error.message };
    }
  },
  getStatuses: async () => {
    console.log('Fetching statuses');
    try {
      const response = await api.get(API_PATHS.STATUSES);
      console.log('Statuses API response:', response.data);
      
      // Handle both array and object responses from the API
      let statusesData = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          statusesData = response.data;
        } else if (typeof response.data === 'object') {
          // If response is an object with results or items property
          if (Array.isArray(response.data.results)) {
            statusesData = response.data.results;
          } else if (Array.isArray(response.data.items)) {
            statusesData = response.data.items;
          } else if (Array.isArray(response.data.statuses)) {
            statusesData = response.data.statuses;
          } else {
            // Try to convert object to array if it has numeric keys
            const possibleArray = Object.values(response.data).filter(item => 
              typeof item === 'object' && (item.status_id || item.id || item.status_name || item.status_code)
            );
            if (possibleArray.length > 0) {
              statusesData = possibleArray;
            }
          }
        }
      }
      
      // Map the data to ensure consistent format
      const mappedStatuses = statusesData.map(status => ({
        status_id: status.status_id || status.id,
        status_code: status.status_code || status.code || status.id,
        status_name: status.status_name || status.name
      }));
      
      return { data: mappedStatuses };
    } catch (error) {
      console.error('Error fetching statuses:', error);
      return { data: [], error: error.message };
    }
  },
  getMediaTypes: async () => {
    console.log('Fetching media types');
    try {
      const response = await api.get(API_PATHS.MEDIA_TYPES);
      console.log('Media types API response:', response.data);
      
      // Handle both array and object responses from the API
      let mediaTypesData = [];
      
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Direct array response
          mediaTypesData = response.data;
        } else if (typeof response.data === 'object') {
          // If response is an object with results or items property
          if (Array.isArray(response.data.results)) {
            mediaTypesData = response.data.results;
          } else if (Array.isArray(response.data.items)) {
            mediaTypesData = response.data.items;
          } else if (Array.isArray(response.data.media_types)) {
            mediaTypesData = response.data.media_types;
          } else {
            // Try to convert object to array if it has numeric keys
            const possibleArray = Object.values(response.data).filter(item => 
              typeof item === 'object' && (item.media_type_channel_id || item.id || item.media_type_channel_name)
            );
            if (possibleArray.length > 0) {
              mediaTypesData = possibleArray;
            }
          }
        }
      }
      
      // Map the data to ensure consistent format
      const mappedMediaTypes = mediaTypesData.map(mediaType => ({
        media_type_channel_id: mediaType.media_type_channel_id || mediaType.id,
        media_type_channel_name: mediaType.media_type_channel_name || mediaType.name
      }));
      
      return { data: mappedMediaTypes };
    } catch (error) {
      console.error('Error fetching media types:', error);
      return { data: [], error: error.message };
    }
  },
  getLegend: async () => {
    console.log('Fetching legend data');
    try {
      const response = await api.get(API_PATHS.LEGEND);
      return response;
    } catch (error) {
      console.error('Error fetching legend data:', error);
      // Return empty structured data instead of mock data
      return { 
        data: {
          brands: [],
          media_types: [],
          campaign_statuses: []
        },
        error: error.message
      };
    }
  },
  
  // Other reference API methods...
};

// API endpoints for data versioning
export const versionApi = {
  getVersions: async (campaignId) => {
    console.log('DEBUG: Fetching versions for campaign ID:', campaignId);
    try {
      // Sử dụng đúng endpoint trong backend
      return await api.get(`/data-versions/?campaign_id=${campaignId}`);
    } catch (error) {
      if (error.response && error.response.status === 500) {
        console.warn('Error fetching versions - likely ID format issue. Returning empty versions.');
        // Return empty data structure to prevent UI errors
        return { data: { count: 0, next: null, previous: null, results: [] } };
      }
      throw error;
    }
  },
  getVersion: (versionId) => {
    console.log('DEBUG: Fetching version details for ID:', versionId);
    return api.get(`/preview_data_version/${versionId}/`);
  },
  // Không cần gọi API createVersion vì backend tự động tạo version khi lưu data
  createVersion: (data) => {
    console.log('DEBUG: Backend automatically creates version when saving accrual data');
    // Backend tự động tạo version khi lưu accrual data, như đã thấy trong logs:
    // [DataVersion Creation - ViewSet] Successfully created DataVersion ID: 76 for campaign 82
    return Promise.resolve({ 
      data: { 
        success: true, 
        message: 'Version created automatically by backend when saving accrual data',
        id: Date.now(), 
        label: data.label 
      } 
    });
  },
  deleteVersion: (versionId) => api.delete(`/data-versions/${versionId}/`),
  restoreVersion: (campaignId, versionId, diffData) => {
    // Dựa trên memory và thông báo lỗi, cần các trường: campaign_id, month, diff, diff_data
    const payload = { 
      campaign_id: campaignId,
      version_id: versionId,
      // Thêm các trường bắt buộc
      month: new Date().toISOString().slice(0, 10), // YYYY-MM-DD format
      // Trường diff bắt buộc (theo thông báo lỗi)
      diff: {
        version_id: versionId,
        restore: true
      },
      diff_data: diffData, // BẮT BUỘC cho backend
      // Flag đặc biệt cho restore
      is_restore: true,
      is_version: false,
      is_history: false,
      media_type: 'Digital amplify' // Thêm media_type nếu cần
    };
    console.log('RESTORE VERSION PAYLOAD (FINAL):', payload);
    
    // Gọi endpoint /data-diff/save/ với đầy đủ các trường bắt buộc
    return api.post(`/data-diff/save/`, payload);
  },
  getVersionData: (versionId) => api.get(`/preview_data_version/${versionId}/`),
  getRestoreLogs: () => api.get('/data-versions/')
};

// API endpoints for import/export
// Gom tất cả các hàm custom gọi API vào object apiMethods
export const apiMethods = {
  // Lấy danh sách vendors cho media type Digital
  getVendors: () => api.get('/category/vendors/'),
  // Lấy danh sách digital ad formats
  getDigitalAdFormats: () => api.get('/category/digital-ad-formats/'),
  // ... các hàm custom khác ...
};

export const importExportApi = {
  // Import snapshot from Excel file
  importSnapshot: (formData, progressCallback) => {
    return api.post('/import/snapshot/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: progressCallback
    });
  },
  

  // Sử dụng validation endpoints đúng như định nghĩa trong backend
  validateCampaignImport: (formData) => {
    console.log('FormData contents for debugging:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ':', pair[1].name || pair[1]);
    }

    // IMPORTANT: Do NOT set Content-Type header manually for file uploads
    // Let the browser set it with the correct boundary parameter
    return api.post('/import/campaigns/validate/', formData, {
      // Prevent Axios from transforming FormData
      transformRequest: [(data) => data],
    }).catch(error => {
      console.error('Error in validateCampaignImport:', error);
      
      // Enhanced error handling
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data || {};
        console.error('Server error response:', errorData);
        
        // Return a structured error response similar to what the API would return
        return {
          data: {
            valid: false,
            error: errorData.detail || errorData.error || 'Server validation failed',
            field_errors: errorData.field_errors || {},
            errors: errorData.errors || [errorData.detail || 'Unknown error'],
            preview: [] // Empty preview in case of error
          }
        };
      } else {
        // Network error or other issues
        return {
          data: {
            valid: false,
            error: error.message || 'Network error during validation',
            errors: [error.message || 'Failed to validate file'],
            preview: [] // Empty preview in case of error
          }
        };
      }
    });
  },
  validateMonthlyDataImport: (formData) => {
    console.log('Monthly data FormData contents:');
    for (let pair of formData.entries()) {
      console.log(pair[0] + ':', pair[1].name || pair[1]);
    }
    
    return api.post('/import/monthly-data/validate/', formData, {
      transformRequest: [(data) => data],
    });
  },
  
  // Import processing endpoints
  importCampaigns: (formData) => api.post('/import/campaigns/process/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  importMonthlyData: (formData) => api.post('/import/monthly-data/process/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Import data with progress tracking
  importWithProgress: (formData, progressCallback) => {
    // Determine which endpoint to use based on import type
    const importType = formData.get('type');
    const endpoint = importType === 'monthly_data' ? 
      '/import_monthly_data/' : 
      '/import_campaigns/';
    
    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        if (progressCallback) {
          progressCallback(percentCompleted);
        }
      }
    });
    
  },
  
  // Combined import function that processes both campaign and monthly data
  // from a single file upload
  combinedImport: async (formData, progressCallback) => {
    try {
      // First import as campaign data
      if (progressCallback) progressCallback(0);
      
      const campaignFormData = new FormData();
      // Copy original form data
      for (let [key, value] of formData.entries()) {
        campaignFormData.append(key, value);
      }
      
      // Step 1: Import as campaign data (0-70% progress)
      const campaignResponse = await api.post('/import/campaigns/process/', campaignFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round((progressEvent.loaded * 70) / progressEvent.total);
          if (progressCallback) progressCallback(percentCompleted);
        }
      });
      
      // Progress update between steps
      if (progressCallback) progressCallback(75);
      
      // Skip the monthly data import for now - campaign import is successful
      if (progressCallback) progressCallback(100);
      
      // Return result with just campaign import info
      return {
        data: {
          success: true,
          combined: false, // Only campaign import was successful
          campaign: campaignResponse.data,
          // Use job ID from campaign response if available
          job_id: campaignResponse.data?.job_id
        }
      };
    } catch (error) {
      console.error('Combined import error:', error);
      throw error;
    }
  },
  
  // Check import status (for background jobs)
  checkImportStatus: (jobId) => api.get(`/import-status/${jobId}/`),
  
  // Get import history
  getImportHistory: (page = 1, pageSize = 10) => 
    api.get('/import-history/', { params: { page, page_size: pageSize } }),
  
  // Delete import history entry
  deleteImportHistory: (importId) => api.delete(`/import-history/${importId}/`),
  
  // Save import as draft
  saveImportDraft: (formData) => api.post('/import-drafts/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Get import drafts
  getImportDrafts: () => api.get('/import-drafts/'),
  
  // Get a specific import draft
  getImportDraft: (draftId) => api.get(`/import-drafts/${draftId}/`),
  
  // Delete import draft
  deleteImportDraft: (draftId) => api.delete(`/import-drafts/${draftId}/`),
  
  // Process import draft
  processImportDraft: (draftId) => api.post(`/import-drafts/${draftId}/process/`),
  
  // Undo last import
  undoLastImport: (importId) => api.post(`/import-history/${importId}/undo/`),
  
  // Download import template - with optional sample data
  downloadImportTemplate: (includeSample = false, mediaTypes = []) => api.get('/api/import/template/', {
    params: { include_sample: includeSample, media_types: mediaTypes.join(',') },
    responseType: 'blob'
  }),
  
  // Get digital vendors for auto-detection
  getDigitalVendors: () => api.get('/category/vendors/'),
  
  // Validate import values against reference data from dimension tables
  validateImportValuesAgainstLegend: async (importData) => {
    console.log('Validating import values against dimension tables (legend)');
    try {
      // Get reference data from dimension tables
      const legendResponse = await referenceApi.getLegend();
      const legendData = legendResponse.data || {};
      
      // If we can't get legend data, consider validation successful to avoid blocking import
      if (!legendData || Object.keys(legendData).length === 0) {
        console.warn('No legend data available, skipping detailed validation');
        return { valid: true, errors: [] };
      }
      
      // Log reference data for debugging
      console.log('Legend data for validation:', {
        brands: (legendData.brands || []).length,
        media_types: (legendData.media_types || []).length,
        statuses: (legendData.campaign_statuses || []).length
      });
      
      // Extract all validation errors
      const validationErrors = [];
      
      // Fields to validate and their corresponding reference data
      const fieldsToValidate = {
        'Brand': legendData.brands || [],
        'Media_Type': legendData.media_types || [],
        'Status': legendData.campaign_statuses || []
      };
      
      // Set of fields that should be skipped if empty
      const optionalFields = new Set(['Status']);
      
      // Map of column names in Excel to field names in the database
      const columnMapping = {
        'Media Type (Channel)': 'Media_Type',
        'Status (Planning/ On-going/Done': 'Status',
        'Digital Vendor': 'Vendor'
      };
      
      // Validate each row in the import data
      importData.forEach((row, rowIndex) => {
        // Map Excel column names to internal field names where needed
        const processedRow = { ...row };
        Object.entries(columnMapping).forEach(([excelCol, fieldName]) => {
          if (row[excelCol] !== undefined) {
            processedRow[fieldName] = row[excelCol];
          }
        });
        
        // Validate each field against reference data
        Object.entries(fieldsToValidate).forEach(([field, referenceList]) => {
          // Skip empty values for optional fields
          if (!processedRow[field] || 
              (typeof processedRow[field] === 'string' && processedRow[field].trim() === '')) {
            if (optionalFields.has(field)) return;
            
            // For required fields that are empty, add validation error
            if (field === 'Brand' || field === 'Media_Type') {
              validationErrors.push({
                row: rowIndex + 1,
                field: field,
                value: '',
                message: `${field} is required`
              });
            }
            return;
          }
          
          // Skip validation if reference list is empty
          if (!referenceList || referenceList.length === 0) return;
          
          // Get the field value from the row
          const importValue = String(processedRow[field]).trim();
          
          // For Media_Type, special case-insensitive comparison and allow partial matches
          if (field === 'Media_Type') {
            // For Media_Type, use looser validation - check if it contains known values
            // This helps with variations like "Digital" vs "DIGITAL" vs "Digital (YouTube)"
            const isValid = referenceList.some(item => {
              const refValue = (item.media_type_name || item.name || '').toLowerCase();
              const importValueLower = importValue.toLowerCase();
              // Allow if the import value contains the reference value or vice versa
              return importValueLower.includes(refValue) || refValue.includes(importValueLower);
            });
            
            if (!isValid) {
              validationErrors.push({
                row: rowIndex + 1,
                field: field,
                value: importValue,
                message: `Value "${importValue}" not found in reference data for ${field}`
              });
            }
          } else {
            // Standard validation for other fields
            const isValid = referenceList.some(item => {
              // Handle different property names based on the field type
              if (field === 'Brand') {
                const refValue = (item.brand_name || item.name || '').toLowerCase();
                return refValue === importValue.toLowerCase();
              } else if (field === 'Status') {
                const refValue = (item.status_name || item.name || '').toLowerCase();
                return refValue === importValue.toLowerCase();
              }
              return false;
            });
            
            if (!isValid) {
              validationErrors.push({
                row: rowIndex + 1,
                field: field,
                value: importValue,
                message: `Value "${importValue}" not found in reference data for ${field}`
              });
            }
          }
        });
        
        // Special handling for Vendor - only required for Digital media type
        // Get Media_Type from either the standard field or the mapped field
        const mediaType = processedRow['Media_Type'] || row['Media Type (Channel)'] || '';
        
        if (typeof mediaType === 'string' && mediaType.toUpperCase().includes('DIGITAL')) {
          const vendorField = 'Vendor';
          const vendorValue = processedRow[vendorField] || row['Digital Vendor'];
          
          // For Digital media type, Vendor is required
          if (!vendorValue || (typeof vendorValue === 'string' && vendorValue.trim() === '')) {
            validationErrors.push({
              row: rowIndex + 1,
              field: vendorField,
              value: '',
              message: `${vendorField} is required for Digital media type`
            });
          }
        }
      });
      
      // Log validation errors for debugging
      if (validationErrors.length > 0) {
        console.warn(`Found ${validationErrors.length} validation errors:`, validationErrors);
      }
      
      return {
        valid: validationErrors.length === 0,
        errors: validationErrors
      };
    } catch (error) {
      console.error('Error validating against legend:', error);
      // In case of an error, still allow the import to proceed
      return {
        valid: true,
        warning: 'Could not validate against reference data, proceeding anyway',
        errors: []
      };
    }
  },
  
  // Column mapping feature is disabled since backend endpoint is not available
  // These methods are implemented to return mock data without making network requests
  
  // Get column mappings without network request
  getColumnMappings: () => {
    console.log('Column mapping is not available - using mock data');
    // Return a pre-resolved promise with empty data
    return new Promise(resolve => {
      resolve({ data: [] });
    });
  },
  
  // Create column mapping without network request
  createColumnMapping: (mappingData) => {
    console.log('Column mapping is not available - using mock data');
    // Return a pre-resolved promise with mock success response
    return new Promise(resolve => {
      resolve({ data: { success: true, id: 'mock-mapping-1' } });
    });
  },
  
  // Export data
  exportData: (params) => {
    const format = params.format || 'excel';
    const type = params.type || 'campaigns';
    const endpoint = `/export/${type}/${format}/`;
    return api.get(endpoint, {
      params,
      responseType: 'blob'
    });
  },
  
  // Process batch imports
  processBatchImport: (batchData) => api.post('/batch-imports/', batchData),
  
  // Get batch import status
  getBatchImportStatus: (batchId) => api.get(`/batch-imports/${batchId}/`),
};

// Hàm tự động tìm và xoá media types không còn trong campaign
export const autoCleanupMediaTypes = async (campaignId, newMediaTypes) => {
  try {
    // 1. Lấy danh sách media types hiện tại của campaign
    const response = await api.get(`/campaigns-data/${campaignId}/media-types/`);
    const existingMediaTypes = response.data || [];

    // 2. Chuẩn hoá tên media types mới (lowercase) - check if newMediaTypes exists
    if (!newMediaTypes || !Array.isArray(newMediaTypes)) {
      console.log('[AUTO] No new media types provided, skipping cleanup');
      return { deleted: [], count: 0 };
    }
    
    const newNames = newMediaTypes.map(mt =>
      (typeof mt === 'string' ? mt : mt.media_type || mt.name || '').toLowerCase()
    );

    // 3. Tìm media types cần xoá (có trong DB nhưng không còn trong danh sách mới)
    const mediaTypesToDelete = existingMediaTypes.filter(existingMt => {
      const existingName = (existingMt.media_type || '').toLowerCase();
      return !newNames.includes(existingName);
    });

    // 4. Xoá từng media type bằng đúng ID (campaignId, mediaTypeId)
    for (const mtToDelete of mediaTypesToDelete) {
      try {
        await mediaTypeApi.deleteMediaType(campaignId, mtToDelete.id);
        console.log(`[AUTO] Đã xoá media type: ${mtToDelete.media_type} (ID: ${mtToDelete.id})`);
      } catch (error) {
        console.error(`[AUTO] Lỗi khi xoá media type ${mtToDelete.media_type}:`, error);
      }
    }
    return { deleted: mediaTypesToDelete.map(mt => mt.media_type), count: mediaTypesToDelete.length };
  } catch (error) {
    console.error('[AUTO] Lỗi khi tự động cleanup media types:', error);
    return { deleted: [], count: 0, error };
  }
};

// API endpoints for users
export const userApi = {
  getAllUsers: () => api.get(API_PATHS.USERS),
  createUser: (userData) => api.post(API_PATHS.USERS, userData),
  updateUser: (userId, userData) => api.patch(`${API_PATHS.USERS}${userId}/`, userData),
  deleteUser: (userId) => api.delete(`${API_PATHS.USERS}${userId}/`),
  assignRole: (userId, roleName) => {
    return api.post(`${API_PATHS.ROLES}assign/`, { user_id: userId, role: roleName }); 
  },
  getRoles: () => api.get(API_PATHS.ROLES),
  getAgencies: () => api.get(API_PATHS.AGENCIES),
  getPermissions: () => api.get(API_PATHS.PERMISSIONS),
};

// API endpoints for category management
export const categoryApi = {
  getCategoryItems: (url) => api.get(url),
  createCategoryItem: (url, data) => api.post(url, data),
  updateCategoryItem: (url, data) => api.put(url, data),
  deleteCategoryItem: (url) => api.delete(url),
};

// API endpoints for dashboards
export const dashboardApi = {
  getDashboardMetrics: () => api.get(`/dashboard/metrics/`),
  getDashboardMetricsSnapshot: (month) => api.get(`/dashboard/metrics/snapshot/?month=${month}`),
  getBrandSummaries: (params) => api.get(API_PATHS.BRAND_SUMMARIES, { params }),
  getBrandSummariesSnapshot: (month) => api.get(`/reports/brand-summaries/snapshot?month=${month}`),
  getBrandSummariesSnapshotSummary: (month) => api.get(`/reports/brand-summaries/snapshot/summary?month=${month}`),
  getSavingsDetailTable: (month) => month ? api.get(`/reports/savings-detail/?month=${month}`) : api.get(`/reports/savings-detail/`),
  getSnapshotMonths: () => api.get(API_PATHS.SNAPSHOT_MONTHS),
  getDashboardBySnapshot: (snapshotMonth) => api.get(`${API_PATHS.SNAPSHOT_BY_MONTH}?month=${snapshotMonth}`),
  // Performance tab endpoints - updated to use existing backend endpoints
  getChannelSummaries: (params) => api.get(API_PATHS.CHANNEL_SUMMARIES, { params }),
  getVarianceRecords: (params) => api.get(API_PATHS.VARIANCE_RECORDS, { params }),
  getMonthlyData: (params) => api.get(API_PATHS.MONTHLY_DATA, { params }),
  // OOH dashboard endpoints
  getOOHCampaignData: (year, snapshotMonth) => {
    const params = {};
    if (year) params.year = year;
    if (snapshotMonth) params.month = snapshotMonth;
    return api.get('/reports/ooh-campaigns/', { params });
  },
  getOOHCampaignDataBySnapshot: (snapshotMonth) => api.get(`/reports/ooh-campaigns/snapshot/`, { params: { month: snapshotMonth } }),
  
  // Budget data endpoints
  getBudgetData: (params = {}) => api.get('/dashboard/budget-data/', { params }),
  getBudgetDataBySnapshot: (snapshotMonth) => api.get(`/dashboard/budget-data/?snapshot_month=${snapshotMonth}`),
};

// API endpoints for total data overview
export const getTotalData = (params = {}) => {
  return api.get('/accrual/total-data/', { params });
};

// Dimension APIs for dropdown data
export const dimensionApi = {
  // Get all available months from DimDate (custom endpoint for unique months)
  getMonths: () => {
    return api.get('/category/dates/months/');
  },
  
  // Get asset video length / image types
  getAssetTypes: () => {
    return api.get('/category/asset-types/', {
      params: {
        page_size: 1000,
        ordering: 'asset_video_length_image_type_name'
      }
    });
  },
  
  // Get sub media types
  getSubMediaTypes: () => {
    return api.get('/category/sub-media-types/', {
      params: {
        page_size: 1000,
        ordering: 'sub_media_type_name'
      }
    });
  },
  
  // Get digital vendors
  getDigitalVendors: () => {
    return api.get('/category/vendors/', {
      params: {
        page_size: 1000,
        ordering: 'digital_vendor_name'
      }
    });
  },
  
  // Get digital ad formats
  getDigitalAdFormats: () => {
    return api.get('/category/digital-ad-formats/', {
      params: {
        page_size: 1000,
        ordering: 'digital_ad_format_name'
      }
    });
  },
  
  // Get OOH formats
  getOOHFormats: () => {
    return api.get('/category/ooh-formats/', {
      params: {
        page_size: 1000,
        ordering: 'ooh_format_name'
      }
    });
  },
  
  // Get all dimension data at once using the legend endpoint
  getLegendData: () => {
    return api.get('/legend-dropdown/');
  }
};

// Export the api instance for direct use
export { api };
export default api; 